import { useParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { extraArticles } from "@/mocks/articles-extra";
import { useSeo, SITE_NAME, SITE_LOGO, getCanonicalUrl } from "@/lib/seo";
import { useJsonLd } from "@/lib/schema";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language;
  const [activeSection, setActiveSection] = useState("");

  const article =
    articles[currentLang]?.[slug || ""] ??
    extraArticles[currentLang]?.[slug || ""];

  const quickAnswers: Record<string, string> = {
    en: "CSSBuy Spreadsheet is an organized resource hub that helps international buyers find products, sellers, shipping guides, QC information, and discount codes all in one place.",
    pt: "O CSSBuy Spreadsheet é um hub de recursos organizado que ajuda compradores internacionais a encontrar produtos, vendedores, guias de envio, informações de QC e códigos de desconto em um só lugar.",
    de: "CSSBuy Spreadsheet ist ein organisierter Ressourcen-Hub, der internationalen Käufern hilft, Produkte, Verkäufer, Versandratgeber, QC-Informationen und Rabattcodes an einem Ort zu finden.",
  };

  const seoData = useMemo(() => {
    if (!article) {
      return {
        title: "Article Not Found - CSSBuy Spreadsheet",
        description: "The requested article could not be found.",
        keywords: "CSSBuy Spreadsheet",
        image: SITE_LOGO,
        type: "website",
        noindex: true,
      };
    }
    return {
      title: `${article.title} | CSSBuy Spreadsheet`,
      description: article.description,
      keywords: "CSSBuy Spreadsheet, CSSBuy guide, CSSBuy resources",
      image: SITE_LOGO,
      type: "article",
    };
  }, [article]);

  useSeo(seoData);

  const articleSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.description,
      image: SITE_LOGO,
      datePublished: article.published,
      dateModified: article.updated,
      author: { "@type": "Organization", name: article.author },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: SITE_LOGO },
      },
      mainEntityOfPage: getCanonicalUrl(location.pathname),
      inLanguage: currentLang,
    };
  }, [article, location.pathname, currentLang]);

  const articleFaqSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    };
  }, [article]);

  useJsonLd("article", articleSchema);
  useJsonLd("article-faq", articleFaqSchema);

  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    if (["en", "pt", "de"].includes(pathLang) && i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
    document.documentElement.lang = i18n.language;
    window.scrollTo(0, 0);
  }, [location.pathname, i18n]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t("notFound.title")}</h1>
            <p className="text-gray-600 mb-6">{t("notFound.desc")}</p>
            <Link to={`/${currentLang}`} className="text-emerald-700 font-medium hover:underline">
              {t("notFound.back")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tocItems = article.sections.map((s) => ({ id: s.id, heading: s.heading }));

  function scrollToSection(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        {/* Hero Article Header */}
        <section className="bg-gray-50 pt-16 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-gray-500">
                <li>
                  <Link to={`/${currentLang}`} className="hover:text-emerald-700 transition-colors">
                    {t("nav.home")}
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-gray-400" />
                </li>
                <li className="text-gray-700">{article.title}</li>
              </ol>
            </nav>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {t("article.meta.published")}: {article.published}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {article.readingTime} {t("article.meta.readingTime")}
              </span>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed">{article.description}</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-[1fr_280px] gap-12">
            {/* Main Content */}
            <div className="order-2 lg:order-1">
              {/* Short Answer */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 mb-10">
                <p className="text-gray-800 leading-relaxed">
                  <strong className="text-emerald-800">Quick Answer:</strong>{" "}
                  {quickAnswers[currentLang] || quickAnswers.en}
                </p>
              </div>

              {/* TOC (mobile) */}
              <div className="lg:hidden mb-10">
                <h3 className="font-semibold text-gray-900 mb-3">{t("article.toc")}</h3>
                <ul className="space-y-2">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`text-sm transition-colors ${
                          activeSection === item.id
                            ? "text-emerald-700 font-medium"
                            : "text-gray-600 hover:text-emerald-700"
                        }`}
                      >
                        {item.heading}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Sections */}
              {article.sections.map((section) => (
                <article key={section.id} id={section.id} className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.heading}</h2>
                  <p className="text-gray-700 leading-relaxed">{section.content}</p>
                </article>
              ))}

              {/* Comparison Table */}
              {article.tableData && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{t("article.comparisonTable")}</h2>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          {article.tableData.headers.map((h) => (
                            <th key={h} className="px-4 py-3 text-left font-semibold text-gray-900 border-b border-gray-200">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {article.tableData.rows.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                            {row.map((cell, j) => (
                              <td key={j} className="px-4 py-3 text-gray-700 border-b border-gray-100">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* FAQ */}
              <div id="faq" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t("article.faqTitle")}</h2>
                <div className="space-y-3">
                  {article.faq.map((item, i) => (
                    <div key={i} className="rounded-xl bg-gray-50 border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-emerald-700 rounded-xl p-8 text-center mb-12">
                <h3 className="text-xl font-bold text-white mb-3">
                  {t("article.readyToExplore")}
                </h3>
                <p className="text-white/80 mb-6">
                  {t("article.findResources")}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-external-link-line" />
                  {t("article.cta")}
                </a>
              </div>

              {/* Internal Link to Home */}
              <div className="border-t border-gray-100 pt-8">
                <p className="text-sm text-gray-500">
                  Back to{" "}
                  <Link to={`/${currentLang}`} className="text-emerald-700 font-medium hover:underline">
                    CSSBuy Spreadsheet Resource Center
                  </Link>
                </p>
              </div>
            </div>

            {/* Sidebar TOC (desktop) */}
            <aside className="order-1 lg:order-2 hidden lg:block">
              <div className="sticky top-24 bg-gray-50 rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">{t("article.toc")}</h3>
                <ul className="space-y-2">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`text-sm text-left w-full transition-colors ${
                          activeSection === item.id
                            ? "text-emerald-700 font-medium"
                            : "text-gray-600 hover:text-emerald-700"
                        }`}
                      >
                        {item.heading}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">{t("article.related")}</h4>
                  <Link
                    to={`/${currentLang}`}
                    className="block text-sm text-gray-600 hover:text-emerald-700 transition-colors mb-2"
                  >
                    CSSBuy Spreadsheet Resource Center
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}